import { useMemo } from "react";
import { useQuery } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { useRouterQueryParams } from "@/react/hooks/useRouterQueryParams";
import type { Room } from "@/react/types/Live/Room";
import { updateOrRemoveQueryParam } from "@circle-react/helpers/urlHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers/internalApi";

const PER_PAGE = 10;

export const useLiveStreamRoomsApi = () => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = useRouterQueryParams();

  const page = useMemo(() => {
    const pageParam = queryParams.page;
    if (typeof pageParam === "string") {
      const parsed = parseInt(pageParam, 10);
      return !isNaN(parsed) && parsed > 0 ? parsed : 1;
    }
    return 1;
  }, [queryParams.page]);

  const updatePage = (newPage: number) => {
    history.push({
      pathname: location.pathname,
      search: updateOrRemoveQueryParam(
        "page",
        newPage === 1 ? undefined : String(newPage),
      ),
    });
  };

  const fetchNextPage = () => {
    updatePage(page + 1);
  };

  const fetchPrevPage = () => {
    updatePage(Math.max(page - 1, 1));
  };

  const { isLoading, isFetching, data, refetch } = useQuery<{
    records: Room[];
    count: number;
    page_count: number;
    has_next_page: boolean;
  }>(
    internalApi.liveStreams.index({
      params: {
        page,
        per_page: PER_PAGE,
      },
    }),
    {
      keepPreviousData: true,
    },
  );

  const records = data?.records || [];
  const totalRecordCount = data?.count ?? 0;
  const pageRecordCount = data?.page_count ?? 0;
  const pageCount = totalRecordCount
    ? Math.ceil(totalRecordCount / PER_PAGE)
    : 0;
  const hasNextPage = !!data?.has_next_page;
  const startRecordNumber = (page - 1) * PER_PAGE + 1;
  const endRecordNumber = startRecordNumber + pageRecordCount - 1;

  return {
    page,
    records,
    totalRecordCount,
    pageRecordCount,
    pageCount,
    hasNextPage,
    startRecordNumber,
    endRecordNumber,
    isLoading,
    isFetching,
    refetch,
    fetchNextPage,
    fetchPrevPage,
  };
};
